import firebase from 'firebase/compat/app';
import 'firebase/compat/functions';
import { formatDuration, intervalToDuration } from 'date-fns';
import firebaseApp, { db } from 'services/firebase';
import i18n, { dateLocale } from 'i18n';

const TOKEN_COLLECTION = 'msi3CmrAccessTokens';
// Share codes are valid for 7 days. The window used to be 60 minutes, which broke the
// common journey: a patient runs CareStart at home, then sees a provider a day or two
// later. Safe to extend because the token is single-use, carries no PHI (identity is
// resolved server-side at redemption), and every redemption is authenticated and audited.
const TOKEN_VALIDITY_HOURS = 168;
// A VisitStart intake code is a different journey and gets a different window than
// the 7-day record code: nothing is gained by leaving an intake live for a week.
//
// 24, not 12: "in the waiting room and seen within the hour" is the best case, not
// the journey. A patient who answers at home the evening before a morning
// appointment, or waits out a long queue and is sent back the next day, was landing
// on a dead code. 24 hours is cheap here — the token is single-use and the doc holds
// no PHI, identity being resolved server-side at redemption.
//
// And 24, not 48, because of an ordering detail: a guest's session gets
// `guestExpiresAt = created + 48h` at BOOT, while this token is minted at
// COMPLETION, some minutes later. A 48-hour code would therefore outlive the very
// session it resolves to and present as valid right up to the moment it resolves to
// nothing. 24 stays strictly inside that window with room to spare.
const VISIT_START_TOKEN_VALIDITY_HOURS = 24;
const REDEEM_FUNCTION = 'redeemHealthRecordShareToken';
const PUSH_DISCHARGE_FUNCTION = 'pushDischargeInstructions';
const CREATE_DISCHARGE_HANDOFF_FUNCTION = 'createDischargeHandoff';
const CLAIM_DISCHARGE_HANDOFF_FUNCTION = 'claimDischargeHandoff';
const KEEP_GUEST_DISCHARGE_FUNCTION = 'keepGuestDischargeInstructions';
const TRANSFER_GUEST_DISCHARGE_FUNCTION = 'transferGuestDischargeInstructions';
const DISCHARGE_HANDOFF_COLLECTION = 'dischargeHandoffs';

// `id` is what gets stored on the token; `label` is display text, read in the current
// language each time it is accessed (careStart:share.sections.*).
const shareableSection = (id) => ({
  id,
  get label() {
    return i18n.t(`careStart:share.sections.${id}`);
  },
});

export const SHAREABLE_SECTIONS = [
  shareableSection('demographics'),
  shareableSection('conditions'),
  shareableSection('surgical'),
  shareableSection('medications'),
  shareableSection('allergies'),
  shareableSection('other'),
];

// The code unlocks a patient's record, so it only ever comes from the CSPRNG (no
// Math.random fallback). Bytes 250-255 are rejected so every digit is equally likely.
const generateManualCode = () => {
  const byte = new Uint8Array(1);
  let code = '';
  while (code.length < 7) {
    crypto.getRandomValues(byte);
    if (byte[0] < 250) code += (byte[0] % 10).toString();
  }
  return code;
};

export const formatManualCode = (code) => {
  if (!code || code.length !== 7) return code || '';
  return `${code.slice(0, 3)} ${code.slice(3)}`;
};

export const normalizeManualCode = (input) => (input || '').replace(/\D/g, '').slice(0, 7);

export const getTokenValidityMs = (validityHours = TOKEN_VALIDITY_HOURS) =>
  validityHours * 60 * 60 * 1000;

export const getTokenValidityLabel = (validityHours = TOKEN_VALIDITY_HOURS) =>
  formatDuration(
    intervalToDuration({ start: 0, end: getTokenValidityMs(validityHours) }),
    { format: ['days', 'hours'], locale: dateLocale() },
  );

/** Hours a VisitStart intake code stays live. See the constant for why it is shorter. */
export const getVisitStartTokenValidityHours = () =>
  VISIT_START_TOKEN_VALIDITY_HOURS;

/**
 * Create a share token. HIPAA: the token doc itself contains NO PHI — only the
 * patient's uid, the consented section IDs, the manual code, and timestamps.
 * Patient name and DOB are resolved server-side at redemption time, so a
 * misconfigured rule on this collection can't leak identity.
 */
export const createShareToken = async ({
  user,
  allowedSections,
  careStartSessionId = null,
  visitStartSessionId = null,
  visitStartLocationId = null,
  validityHours = TOKEN_VALIDITY_HOURS,
}) => {
  if (!user?.uid) throw new Error('Not signed in.');
  const enabled = Object.entries(allowedSections || {})
    .filter(([, on]) => !!on)
    .map(([id]) => id);

  const manualCode = generateManualCode();
  const expiresAtMs = Date.now() + getTokenValidityMs(validityHours);

  const docRef = await db.collection(TOKEN_COLLECTION).add({
    patientUserId: user.uid,
    manualCode,
    // A VisitStart guest passes {} here and every section comes back withheld: they
    // have no record to share, only the intake they just answered.
    allowedSections: enabled,
    // Reference only — the CareStart results live under the patient's account
    // (users/{uid}/careStartSessions). The redeem function resolves them server-side, so the
    // token doc itself stays free of clinical content.
    careStartSessionId: careStartSessionId || null,
    // Same, for a VisitStart pre-visit intake. That session is never nested under
    // the patient, so the redeem function additionally checks the session's own
    // sessionActor.patientUid before handing anything over.
    visitStartSessionId: visitStartSessionId || null,
    // Which collection to find it in: `locations/{id}/visitStartSessions` for a
    // check-in started from a clinic's poster, or the top-level collection when
    // null. A lookup hint, not a grant — a clinic id here widens nothing, because
    // the ownership check above is what decides whether the intake is handed over.
    visitStartLocationId: visitStartLocationId || null,
    expiresAt: new Date(expiresAtMs),
    used: false,
    createdAt: firebase.firestore.FieldValue.serverTimestamp(),
  });

  return { tokenId: docRef.id, manualCode, expiresAtMs };
};

/**
 * Revoke a token the patient previously generated (sets used=true so it can no
 * longer be redeemed). Safe to call even if the token has already been used.
 */
export const revokeShareToken = async (tokenId) => {
  if (!tokenId) return;
  await db.collection(TOKEN_COLLECTION).doc(tokenId).update({ used: true });
};

/**
 * QR payload. We encode a deep link to the public /share landing page so a
 * provider can scan with any phone camera and be guided to view the record (or
 * create an account). The URL carries only the 7-digit manual code, which by
 * itself is NOT PHI (no patient identifiers) — identity is resolved server-side
 * at redemption. Our own in-app scanner reads the same URL via parseScannedShareCode.
 */
export const buildQrPayload = ({ manualCode }) => {
  const origin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : '';
  return `${origin}/share?c=${encodeURIComponent(manualCode)}`;
};

/**
 * Pull a 7-digit share code out of a scanned QR string. Handles the current
 * /share?c=NNNNNNN URL, the legacy JSON payload (codes minted before the URL
 * switch are still valid within their 60-minute window), and a bare 7-digit
 * string. Returns the normalized code, or null when none is present.
 */
export const parseScannedShareCode = (raw) => {
  if (!raw) return null;

  // Current form: an absolute /share?c=1234567 URL.
  try {
    const fromUrl = normalizeManualCode(new URL(raw).searchParams.get('c'));
    if (fromUrl.length === 7) return fromUrl;
  } catch {
    // not a URL — fall through
  }

  // Legacy form: the JSON payload we used to encode.
  try {
    const fromJson = normalizeManualCode(JSON.parse(raw)?.manualCode);
    if (fromJson.length === 7) return fromJson;
  } catch {
    // not JSON — fall through
  }

  const digits = normalizeManualCode(raw);
  return digits.length === 7 ? digits : null;
};

/**
 * Redeem a 7-digit manual code via Cloud Function. The function uses the admin
 * SDK to look up the token, validate it, mark it used, fetch the patient's
 * consented record sections, and write an audit log entry. The client never
 * reads the token doc or the patient's user doc directly.
 */
export const consumeShareCodeAndFetchRecord = async ({ manualCode }) => {
  const cleanCode = normalizeManualCode(manualCode);
  if (cleanCode.length !== 7) {
    throw new Error('Enter the full 7-digit code.');
  }

  const callable = firebaseApp.functions().httpsCallable(REDEEM_FUNCTION);
  try {
    const { data } = await callable({ manualCode: cleanCode });
    return data;
  } catch (err) {
    // Surface the function's message verbatim so the user sees the actual cause
    // (expired, used, unknown code) instead of a generic error.
    throw new Error(err?.message || 'Could not validate that code.');
  }
};

/**
 * Reverse direction of consumeShareCodeAndFetchRecord: send finalized discharge
 * instructions back to a patient whose record was imported into this encounter.
 * No code/QR needed — the provider already holds the patient's uid. The Cloud
 * Function re-verifies consent (a prior redemption audit row) before writing to
 * the patient's server-write-only dischargeInstructions subcollection.
 */
export const sendDischargeInstructions = async ({ patientUserId, discharge }) => {
  if (!patientUserId) throw new Error('No imported patient to send to.');
  const callable = firebaseApp.functions().httpsCallable(PUSH_DISCHARGE_FUNCTION);
  try {
    const { data } = await callable({ patientUserId, discharge });
    return data;
  } catch (err) {
    throw new Error(err?.message || 'Could not send discharge instructions.');
  }
};

/**
 * Patient side of the partner API: open a record a hospital sent to this patient.
 * The code works once; what comes back is reviewed before anything is saved.
 */
export const openHospitalRecord = async ({ manualCode }) => {
  const cleanCode = normalizeManualCode(manualCode);
  if (cleanCode.length !== 7) {
    throw new Error('Please enter the full 7-digit code.');
  }
  const callable = firebaseApp.functions().httpsCallable('openSharedRecordForPatient');
  try {
    const { data } = await callable({ manualCode: cleanCode });
    return data;
  } catch (err) {
    throw new Error(err?.message || 'Could not open that record.');
  }
};

/**
 * Send the finished visit back to the partner system (e.g. a clinic's HMIS) that pushed
 * this patient in through the partner API. The partner fetches it by its own request id.
 */
export const sendSharedRecordResult = async ({ importId, ...result }) => {
  const callable = firebaseApp.functions().httpsCallable('sendSharedRecordResult');
  try {
    const { data } = await callable({ importId, ...result });
    return data;
  } catch (err) {
    throw new Error(err?.message || 'Could not send the visit.');
  }
};

/**
 * QR path for a patient whose record was NOT imported: park the instructions on a
 * one-time handoff the patient's phone claims by scanning. See
 * functions/src/accounts/dischargeHandoff.js for why the id is the credential and
 * why it stops being one the moment it is used.
 * @returns {Promise<{ handoffId: string, expiresAt: number }>}
 */
export const createDischargeHandoff = async ({ discharge }) => {
  const callable = firebaseApp.functions().httpsCallable(CREATE_DISCHARGE_HANDOFF_FUNCTION);
  try {
    const { data } = await callable({ discharge });
    return data;
  } catch (err) {
    throw new Error(err?.message || 'Could not prepare the code for the patient.');
  }
};

/** The URL the provider's QR carries. A plain https link, so any phone camera opens it. */
export const buildDischargeHandoffUrl = (handoffId) =>
  `${window.location.origin}/discharge?h=${encodeURIComponent(handoffId)}`;

/**
 * Watch a handoff from the provider's side, for the moment the patient scans it.
 * The rules only let the sending provider read it, and only by id.
 */
export const subscribeDischargeHandoff = (handoffId, onChange, onError) =>
  db
    .collection(DISCHARGE_HANDOFF_COLLECTION)
    .doc(handoffId)
    .onSnapshot(
      (snap) => onChange(snap.exists ? snap.data() : null),
      (err) => onError?.(err),
    );

/**
 * Patient side: take the instructions into the signed-in (possibly anonymous)
 * account. Idempotent for the phone that already claimed it.
 * @returns {Promise<{ id: string, guest: boolean }>}
 */
export const claimDischargeHandoff = async (handoffId) => {
  const callable = firebaseApp.functions().httpsCallable(CLAIM_DISCHARGE_HANDOFF_FUNCTION);
  try {
    const { data } = await callable({ handoffId });
    return data;
  } catch (err) {
    const wrapped = new Error(err?.message || 'Could not open these instructions.');
    wrapped.code = err?.code;
    throw wrapped;
  }
};

/** After a guest creates an account: stop their scanned instructions expiring. */
export const keepGuestDischargeInstructions = async () => {
  const callable = firebaseApp.functions().httpsCallable(KEEP_GUEST_DISCHARGE_FUNCTION);
  const { data } = await callable({});
  return data;
};

/**
 * A guest who scanned, then signed in to an existing account on the same phone:
 * move their copy across. Needs the secret the claim handed only to this browser.
 */
export const transferGuestDischargeInstructions = async ({
  handoffId,
  guestUid,
  transferSecret,
}) => {
  const callable = firebaseApp.functions().httpsCallable(TRANSFER_GUEST_DISCHARGE_FUNCTION);
  try {
    const { data } = await callable({ handoffId, guestUid, transferSecret });
    return data;
  } catch (err) {
    const wrapped = new Error(err?.message || 'Could not move these instructions to your account.');
    wrapped.code = err?.code;
    throw wrapped;
  }
};
